import { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  KanbanTag,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  PersonMark,
  Table,
  type Column,
} from '@evogard/ui';
import type { ClaimSummary } from '@evogard/contract';
import {
  analysisHoldLabel,
  format as formatMoney,
  formatSpan,
  fromColumn,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { ClaimDeskModal } from '../claim/ClaimDeskModal.js';
import { showUndoToast, type LastMove } from '../claim/UndoMove.js';
import { onClaimChanged } from '../claim/useDesk.js';
import { REPAIR_DESK } from '../claim/desk.js';
import { DataCell } from '../DataCell.js';
import { useClaimList } from '../claim/useClaims.js';
import { useSlaSummary } from '../claim/useDeadlines.js';
import { slaLabel } from '../claim/ClaimDeadlines.js';
import { ClaimLabelTags } from '../claim/ClaimLabels.js';

/**
 * A fila do supervisor do Reparo. Eram duas até 03/11/2026 — a Supervisão, na
 * entrada, e a Aprovação Supervisor —; a Supervisão saiu, e sobrou a decisão
 * com o orçamento pronto: reparar, fazer acordo ou indenizar.
 */
const QUEUE = {
  phase: 'aprovacao_supervisor',
  label: 'Reparo, acordo ou indenização',
  empty: 'Nenhum orçamento esperando decisão.',
  emptyHint:
    'Quando a Orçamentação manda um orçamento para aprovação, ele chega aqui para decidir entre o reparo, o acordo e a indenização.',
} as const;

/**
 * Supervisão › Supervisão do Reparo (29/10/2026, decisão do dono): no lugar do
 * quadro, uma fila em linhas — a mais antiga primeiro,
 * porque o supervisor trabalha por ordem de chegada e por prazo. Clicar numa
 * linha abre a mesma mesa das esteiras (`ClaimDeskModal`).
 *
 * O card continua no departamento Reparo: a fila é a lista de sinistros com
 * `supervisionStages=somente` e a fase da Aprovação. A API esconde a fase de
 * quem não supervisiona, e a rota já cobra `claims.repairs:supervisionar`.
 */
export function RepairSupervisionScreen() {
  const { data: context } = useSessionContext();
  const [opened, setOpened] = useState<ClaimSummary | null>(null);
  const [lastMove, setLastMove] = useState<LastMove | null>(null);
  /** Sobe a cada ato da mesa, e o número de SLA expirado recarrega. */
  const [version, setVersion] = useState(0);
  useEffect(() => onClaimChanged(() => setVersion((v) => v + 1)), []);

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  /* A mesa invalida as listas de sinistro a cada ato: a fila recarrega sozinha. */
  const list = useClaimList({
    search: term,
    currentDepartment: 'reparo' as const,
    currentPhase: QUEUE.phase,
    supervisionStages: 'somente' as const,
    includeArchived: false,
    page,
    perPage,
  });
  const sla = useSlaSummary('reparo', version);
  const expired = sla.data?.find((s) => s.phase === QUEUE.phase)?.slaExpired ?? 0;

  return (
    <Page measure="cheia">
      <PageHeader title="Supervisão do Reparo">
        Os sinistros de {context?.currentTenant.nome} que esperam a decisão do supervisor
        do reparo, do que espera há mais tempo para o mais novo.
      </PageHeader>

      {expired > 0 && (
        <Alert tone="alerta">
          {expired === 1
            ? '1 sinistro desta fila está com o SLA expirado.'
            : `${expired} sinistros desta fila estão com o SLA expirado.`}
        </Alert>
      )}
      {list.isError && <Alert tone="erro">{list.error.message}</Alert>}
      {(!list.isError || list.data) && (
        <Card bleed>
          <Table
            paginate={false}
            search={{
              label: `Pesquisar em ${QUEUE.label}`,
              placeholder: 'Qualquer dado do sinistro: nome, placa, relato, protocolo…',
              value: search,
              onChange: (value: string) => {
                setSearch(value);
                setPage(1);
              },
            }}
            label={QUEUE.label}
            items={list.data?.items ?? []}
            loading={list.isLoading}
            skeletonRows={perPage}
            rowKey={(c) => String(c.id)}
            rowTitle={(c) => `Sinistro #${c.id}`}
            rowIcon="escudo"
            /* A linha inteira abre a mesa; o teclado chega pelo Abrir. */
            onRowClick={setOpened}
            rowActions={(c) => [
              { label: 'Abrir', icon: 'olho', run: () => setOpened(c) },
            ]}
            empty={
              term
                ? 'Nenhum sinistro desta fila tem o que foi pesquisado.'
                : `${QUEUE.empty} ${QUEUE.emptyHint}`
            }
            columns={columns()}
          />
          {list.data && (
            <Pagination
              page={list.data.page}
              perPage={list.data.perPage}
              total={list.data.total}
              onChange={setPage}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
              label="sinistros"
              className="border-t border-borda-suave px-4 py-3"
            />
          )}
        </Card>
      )}

      <ClaimDeskModal
        key={opened?.id ?? 'fechado'}
        claim={opened}
        onClose={() => {
          /* Fechar a mesa com o Desfazer valendo leva o aviso para o canto. */
          if (lastMove) showUndoToast(lastMove);
          setLastMove(null);
          setOpened(null);
        }}
        onOpenClaim={setOpened}
        onMoved={(move) => {
          /* Moveu, a mesa fecha, como nas esteiras (15/10/2026): o sinistro
             saiu da fila, e o Desfazer vai para o canto da tela. */
          showUndoToast(move);
          setLastMove(null);
          setOpened(null);
        }}
        lastMove={lastMove}
        onLastMoveDone={() => setLastMove(null)}
      />
    </Page>
  );
}

/**
 * As colunas da fila (29/10/2026, pedido do dono: a tabela no lugar da lista
 * em seções). São as da visão em tabela dos quadros, mais o que o supervisor
 * precisa para decidir sem abrir a mesa.
 *
 * **Cabem na tela, sem rolar de lado (30/10/2026).** Eram quatorze colunas na
 * entrada e dezessete na decisão, e a tabela pedia o dobro da largura da mesa.
 * Ficaram oito, cada uma no que a decisão pesa: a oficina, o orçamento e
 * quanto ele é da FIPE (a FIPE em si vai na dica do percentual). O número e o
 * nome dividem uma coluna, numa linha só, como manda o `DataCell`. Ocorrido,
 * local, relato e protocolo saíram da fila e continuam na mesa, a um clique.
 *
 * O texto livre (nome, modelo, oficina, tipo) vai dentro de um
 * `contain-inline-size`: com o `table-layout: auto` da `Table`, o `truncate`
 * sozinho não corta nada — a coluna cresce até caber o texto inteiro, e um
 * modelo de carro comprido empurrava a tabela para fora da tela. Com a
 * contenção o texto deixa de ditar a largura mínima: a coluna ocupa o que
 * sobra, corta só quando falta espaço, e nunca abaixo do `minWidth`. Um teto
 * fixo (`max-w-*`) cortava o nome mesmo em tela larga. A fatia da sobra
 * vem do `width` percentual: sem ele, quem leva o espaço são as colunas que
 * não contêm o texto. O texto cortado vai inteiro na dica.
 */
function columns(): Array<Column<ClaimSummary>> {
  return [
    {
      title: 'Sinistro',
      alignment: 'inicio',
      minWidth: '12rem',
      width: '22%',
      cell: (c) => {
        const name = c.type === 'terceiro' ? c.thirdParty?.name : c.vehicle?.memberName;
        const kind =
          c.type === 'terceiro' ? `terceiro do #${c.parentClaimId}` : 'associado';
        return (
          <div className="contain-inline-size">
            <DataCell
              icon={c.type === 'terceiro' ? 'terceiro' : 'pessoa'}
              hint={`Sinistro #${c.id}, ${kind}${name ? ` — ${name}` : ''}`}
            >
              <span className="font-semibold tabular-nums">#{c.id}</span>
              {name ? <span className="truncate">{name}</span> : <NotProvided />}
            </DataCell>
          </div>
        );
      },
    },
    {
      title: 'Veículo',
      alignment: 'inicio',
      minWidth: '9rem',
      width: '20%',
      cell: (c) => {
        const plate = c.type === 'terceiro' ? c.thirdParty?.plate : c.vehicle?.plate;
        const model = (
          c.type === 'terceiro'
            ? [c.thirdParty?.model]
            : [c.vehicle?.brand, c.vehicle?.model, c.vehicle?.modelYear]
        )
          .filter(Boolean)
          .join(' ');
        return (
          <div className="contain-inline-size">
            <DataCell icon="veiculo" hint={model || undefined}>
              {plate ? (
                <span className="font-mono font-medium tabular-nums">{plate}</span>
              ) : (
                <span className="text-texto-suave">Sem placa</span>
              )}
              {model && <span className="truncate text-texto-suave">{model}</span>}
            </DataCell>
          </div>
        );
      },
    },
    {
      title: 'Oficina',
      alignment: 'inicio' as const,
      minWidth: '8rem',
      width: '14%',
      cell: (c: ClaimSummary) =>
        c.supervisionQueue?.workshop ? (
          <div className="contain-inline-size">
            <DataCell icon="chave" hint={c.supervisionQueue.workshop}>
              <span className="truncate">{c.supervisionQueue.workshop}</span>
            </DataCell>
          </div>
        ) : (
          <NotProvided />
        ),
    },
    {
      title: 'Orçamento',
      alignment: 'fim' as const,
      minWidth: '6.5rem',
      cell: (c: ClaimSummary) => {
        const amount = fromColumn(c.supervisionQueue?.grossTotal);
        return amount !== null ? (
          <span className="tabular-nums">{formatMoney(amount)}</span>
        ) : (
          <NotProvided />
        );
      },
    },
    {
      /* Quanto o orçamento é da FIPE: o número que pesa entre reparar e indenizar. */
      title: '% da FIPE',
      alignment: 'fim' as const,
      minWidth: '5rem',
      cell: (c: ClaimSummary) => {
        const share = shareOfFipe(c);
        const fipe = fromColumn(c.supervisionQueue?.fipeValue);
        return share !== null ? (
          <span
            className="tabular-nums"
            title={fipe !== null ? `FIPE: ${formatMoney(fipe)}` : undefined}
          >
            {share}%
          </span>
        ) : null;
      },
    },
    {
      title: 'Na fase',
      minWidth: '6.5rem',
      /* O SLA da fase; sem prazo configurado, há quanto tempo espera. */
      cell: (c) => {
        if (!c.phaseEnteredAt) return null;
        const sla = c.phaseDeadlineAt
          ? slaLabel(c.phaseEnteredAt, c.phaseDeadlineAt)
          : null;
        return sla && sla.tone !== 'neutro' ? (
          <KanbanTag icon="relogio" tone={sla.tone} title="SLA da fase">
            {sla.text}
          </KanbanTag>
        ) : (
          <span className="text-sm text-texto-suave">
            há {formatSpan(Date.now() - new Date(c.phaseEnteredAt).getTime())}
          </span>
        );
      },
    },
    {
      title: 'Responsável',
      alignment: 'inicio',
      minWidth: '8rem',
      cell: (c) => (
        <PersonMark
          name={c.assignee?.name ?? null}
          emptyLabel={REPAIR_DESK.noAssignee}
          className="text-sm"
        />
      ),
    },
    {
      title: 'Etiquetas',
      alignment: 'inicio',
      minWidth: '7rem',
      /* As marcas que pedem atenção, junto das etiquetas do setor. */
      cell: (c) => (
        <div className="flex flex-wrap gap-1">
          {c.type !== 'terceiro' && c.involvesThirdParty && (
            <KanbanTag>com terceiro</KanbanTag>
          )}
          {c.hold && (
            <KanbanTag tone="alerta">Parado: {analysisHoldLabel(c.hold)}</KanbanTag>
          )}
          <ClaimLabelTags labels={c.labels} />
        </div>
      ),
    },
  ];
}

/** O orçamento sobre a FIPE, em pontos percentuais inteiros. */
function shareOfFipe(c: ClaimSummary): number | null {
  const fipe = fromColumn(c.supervisionQueue?.fipeValue);
  const amount = fromColumn(c.supervisionQueue?.grossTotal);
  return amount !== null && fipe !== null && fipe > 0
    ? Math.round((amount / fipe) * 100)
    : null;
}
